import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./../../../assets/css/general/ForgotPassword/ForgotPassword.css";
import forgotImage from "./../../../assets/images/forgotpasswordImage.png";

const IconKey = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="7.5" cy="15.5" r="5.5" />
    <path d="M21 2l-9.6 9.6" />
    <path d="M15.5 7.5l3 3L22 7l-3-3" />
  </svg>
);

const IconArrowLeft = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 12H5" />
    <path d="M12 19l-7-7 7-7" />
  </svg>
);

const IconCheck = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setPageLoading(false), 500);
    return () => clearTimeout(t);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!email.trim()) {
      setErrorMsg("Please enter your email address.");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }

    setIsLoading(true);

    // Replace with your actual API endpoint
    const fd = new FormData();
    fd.append("email", email);

    axios
      .post(
        `${process.env.REACT_APP_BASE_URL || ""}/student/forgot-password`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      )
      .then(() => {
        setIsLoading(false);
        setIsSuccess(true);
      })
      .catch((err) => {
        setIsLoading(false);
        const data = err.response?.data;
        if (data?.message) {
          setErrorMsg(data.message);
        } else if (data?.errors?.email) {
          setErrorMsg(data.errors.email[0]);
        } else {
          setErrorMsg("Something went wrong. Please try again.");
        }
      });
  };

  if (pageLoading) {
    return (
      <div className="fp-loader">
        <div className="fp-page-spinner" />
      </div>
    );
  }

  return (
    <div className="fp-page">
      {/* ══════ LEFT — 62% image + text ══════ */}
      <div className="fp-left">
        {/* Decorative ? marks */}
        <span className="fp-deco-q q1" aria-hidden="true">
          ?
        </span>
        <span className="fp-deco-q q2" aria-hidden="true">
          ?
        </span>
        <span className="fp-deco-q q3" aria-hidden="true">
          ?
        </span>

        {/* Image — full cover */}
        <div className="fp-illus-wrap">
          <img src={forgotImage} alt="Forgot password" className="fp-illus" />
        </div>

        {/* Text overlay */}
        <div className="fp-left-inner">
          <p className="fp-lt-top">No worries,</p>
          <h2 className="fp-lt-title">
            We'll help you
            <br />
            get back in.
          </h2>
          <p className="fp-lt-sub">
            Enter your email and we'll send you a reset link.
          </p>
        </div>
      </div>

      {/* ══════ RIGHT — 38% card ══════ */}
      <div className="fp-right">
        <div className="fp-card">
          {/* Icon */}
          <div className="fp-card-header">
            <div className="fp-card-icon">
              <IconKey />
            </div>
            <h1 className="fp-title">
              Forgot Your
              <br />
              Password?
            </h1>
            <p className="fp-desc">
              Enter the email address linked to your account and we'll send you
              a password reset link.
            </p>
          </div>

          {/* Success message */}
          {isSuccess && (
            <div className="fp-success" role="status">
              <IconCheck />
              <span>
                Reset link sent! Check your inbox at <strong>{email}</strong>.
              </span>
            </div>
          )}

          {/* Error message */}
          {errorMsg && (
            <div className="fp-error" role="alert">
              {errorMsg}
            </div>
          )}

          {/* Form — hide after success */}
          {!isSuccess && (
            <form onSubmit={handleSubmit} noValidate>
              <div className="fp-field">
                <label className="fp-label" htmlFor="fpEmail">
                  Email Address
                </label>
                <input
                  className="fp-input"
                  id="fpEmail"
                  type="email"
                  placeholder="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  autoFocus
                />
              </div>

              <button type="submit" className="fp-btn" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="fp-btn-spinner" aria-hidden="true" />{" "}
                    Sending…
                  </>
                ) : (
                  "Reset Password"
                )}
              </button>
            </form>
          )}

          {/* Back to login */}
          <button
            type="button"
            className="fp-back"
            onClick={() => navigate("/login")}
          >
            <IconArrowLeft />
            Back to signin
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
